import { consentIdentity } from '../api';
import applyConsent, { bootstrapWebAnalytics } from './applyConsent';
import { getAttribution } from './attribution';
import { getConsent, onConsentChange } from './consentState';
import { collectTrackingIds } from './trackingIds';

let markAnalyticsReady;
const analyticsReady = new Promise((resolve) => {
  markAnalyticsReady = resolve;
});

/**
 * Resolves once initConsent has set up the vendors for the stored decision
 * (GA4 SDK booted, Meta pixel initialised). Events logged before that are
 * dropped: FirebaseBaseEvent bails without window.googleAnalytics and the
 * pixel ignores calls made before init. Never resolves where initConsent does
 * not run (SSR, unit tests).
 * @returns {Promise<void>}
 */
export const whenAnalyticsReady = () => analyticsReady;

/**
 * Boot-time consent wiring: apply the stored decision (if any) and keep
 * vendors in sync with future changes. Called once from app.js.
 */
const initConsent = () => {
  if (typeof window === 'undefined') return;
  const consent = getConsent();
  // Advanced consent mode: GA4 boots even without a stored decision, so an
  // unanswered banner still produces cookieless pings under the SSR 'denied'
  // default. applyConsent re-runs the same (idempotent) setup for the vendors
  // a stored decision affects.
  const vendorSetup = [bootstrapWebAnalytics()];
  if (consent) vendorSetup.push(applyConsent(consent));
  // Both catch their own errors; a failed setup must not hold events forever.
  Promise.all(vendorSetup).then(markAnalyticsReady, markAnalyticsReady);
  onConsentChange((next) => {
    // Capture the ad ids BEFORE applyConsent deletes the vendor cookies — the
    // server needs them to revoke a logged-out session's identity mapping
    // (GDPR Art. 7(3)); logged-in revokes match by user id instead.
    const ids = collectTrackingIds();
    applyConsent(next);
    const call = next.marketing
      ? consentIdentity({ action: 'sync', ids, attribution: getAttribution() })
      : consentIdentity({ action: 'revoke', ids });
    call.catch(() => {});
  });
};

export default initConsent;
